<template>
  <div :class="['product-card', { 'single-image': images.length === 1}]">
    <ui-loader
      size="60"
      v-if="LOADING"
    />
    <div class="content">
      <div class="info-header">
        <product-markers
          :markers="PRODUCT.markers"
          :plates="PRODUCT.plates"
        />
        <h1 class="title">
          <span>{{ PRODUCT.name }}</span>
          {{ PRODUCT.code }}
        </h1>
        <favorite-button
          :product-id="PRODUCT.id"
          :product-code="PRODUCT.code"
          :product-price="currentPrice"
          :sku-id="ACTIVE_SKU.id"
          @change="onChangeFavorite"
        />
      </div>

      <vue-client-only>
        <div class="gallery">
          <swiper
            v-if="adaptive.isMobile"
            :items="images"
            v-slot="{ item }"
            :slides-to-show="1"
            dots
            @change="onChangeImageSlide"
          >
            <media :media="item" width="400" :alt="PRODUCT.name" />
          </swiper>
          <div
            v-else
            class="image-container"
            v-for="image in images"
            :key="image.id"
          >
            <media :media="image" :width="images.length > 1 ? 400 : 800" :alt="PRODUCT.name" />
          </div>
        </div>
      </vue-client-only>

      <div class="info-content">
        <div class="prices">
          <div :class="['price price-rub', { discount: isDiscount }]">
            {{ currentPrice | price }}
          </div>
          <div
            v-if="isDiscount"
            class="old-price"
          >
            {{ PRODUCT.price.basePrice | price }}
          </div>
        </div>

        <select-color
          show-color-name
          label="Цвета"
          :colors="COLORS"
          :active-color="ACTIVE_COLOR"
          @change="onChangeColor"
        />

        <select-size
          visible-hint
          label="Размеры"
          :sizes="SIZES"
          :active-sku="ACTIVE_SKU"
          @change="chooseSize"
        >
          <size-table
            v-if="PRODUCT.sizeTable"
            :product-id="PRODUCT.id"
            @open="onOpenSizeTable"
          />
        </select-size>

        <to-cart
          class="product-card__to-cart"
          :product="PRODUCT"
          :sku-id="ACTIVE_SKU.id"
          :price="PRODUCT.price.basePrice"
          :discount-price="PRODUCT.price.discountPrice"
          :text-add="csmToken && 'Добавить в заказ в CSM'"
          @add="onAddProductToCart"
          @to-cart="onRedirectToCart"
        />

        <ui-notification
          class="sold-out"
          v-if="!PRODUCT.available"
          title="Товар распродан"
        />

        <product-delivery
          :active-sku="ACTIVE_SKU"
          :availability-stores="AVAILABILITY_LIST"
          class="product-card__product-delivery"
          @click="onClickProductDelivery"
        />

        <product-bonus :bonus="PRODUCT.bonus" />

        <info-menu
          :product-code="PRODUCT.code"
          :city-name="CITY.name"
          :active-sku="ACTIVE_SKU"
          class="product-card__info"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import VueClientOnly from 'vue-client-only';


/* Components */
import ProductMarkers from './product-markers.vue';
import selectSize from './select-size.vue';
import SelectColor from './select-color.vue';
import ToCart from './to-cart.vue';
import ProductDelivery from './product-delivery.vue';
import ProductBonus from './product-bonus.vue';
import SizeTable from './size-table.vue';
import FavoriteButton from '@/components/favorite-button/favorite-button.vue';
import InfoMenu from '@/components/product/info-menu/info-menu.vue';
import { getCsmToken } from '@/utils/csm-cookies';
import { productGtmEvents } from '@/gtm/product';
import { DELIVERY_TYPES } from '@/constants/delivery';

export default {
  name: 'product',

  components: {
    ProductMarkers,
    selectSize,
    SelectColor,
    ToCart,
    ProductDelivery,
    ProductBonus,
    SizeTable,
    FavoriteButton,
    InfoMenu,
    VueClientOnly,
  },

  inject: ['adaptive'],

  data() {
    return {
      csmToken: getCsmToken(),
    }
  },

  computed: {
    ...mapGetters({
      LOADING: 'product/loading',
      PRODUCT: 'product/item',
      COLORS: 'product/colors',
      ACTIVE_COLOR: 'product/activeColor',
      ACTIVE_SKU: 'product/activeSku',
      SIZES: 'product/sizes',
      CITY: 'user/city',
      AVAILABILITY_LIST: 'product/availability',
    }),

    images() {
      return this.PRODUCT.media?.filter(({ type }) => type === 'photo') || [];
    },

    currentPrice() {
      return this.PRODUCT.price?.discountPrice ||
          this.PRODUCT.price?.basePrice;
    },

    isDiscount() {
      return Number(this.PRODUCT.price?.discountRate);
    },
  },

  watch: {
    ACTIVE_COLOR() {
      this.writeQueryParams();
    }
  },

  mounted() {
    if (this.SIZES.length === 1 && this.SIZES[0].available) {
      this.chooseSize(this.SIZES[0])
    }

    if (this.ACTIVE_SKU?.id) {
      this.onShowProductDelivery();
    }
  },

  methods: {
    ...mapActions({
      CHOOSE_COLOR: 'product/chooseColor',
    }),

    writeQueryParams(size = {}) {
      this.$router.replace({
        name: 'Product',
        params: { id: this.ACTIVE_COLOR },
        query: size.available ? { 'sku-id': size.id } : {},
      });
    },

    chooseSize(size) {
      if (size.id === this.ACTIVE_SKU.id) return;

      this.writeQueryParams(size);

      productGtmEvents.card.chooseSize({
        productCode: this.PRODUCT?.code,
        size: size.size,
      });

      this.onShowProductDelivery();
    },

    onChangeFavorite(value) {
      productGtmEvents.card.toggleFavorite({ value, productCode: this.PRODUCT?.code });
    },

    onChangeImageSlide() {
      productGtmEvents.card.scrollPhoto({ productCode: this.PRODUCT?.code });
    },

    async onChangeColor(value) {
      await this.CHOOSE_COLOR(value);
      productGtmEvents.card.chooseColor({ productCode: this.PRODUCT?.code });
      this.onShowProductDelivery();
    },

    onAddProductToCart() {
      productGtmEvents.card.addToCart({ productCode: this.PRODUCT?.code });
    },

    onRedirectToCart() {
      // Google Tag Manager event
      productGtmEvents.card.redirectToCart();
    },

    onOpenSizeTable() {
      productGtmEvents.card.openSizeTable({ productCode: this.PRODUCT?.code });
    },

    onShowProductDelivery() {
      productGtmEvents.card.showDelivery({
        productCode: this.PRODUCT?.code,
        cityName: this.CITY?.name,
        activeSku: this.ACTIVE_SKU,
      });
    },

    onClickProductDelivery({ name, deliveryInterval, storesCount }) {
      productGtmEvents.card.chooseDelivery({
        deliveryType: name,
        productCode: this.PRODUCT?.code,
        cityName: this.CITY?.name,
        size: this.ACTIVE_SKU?.size,
        deliveryInterval,
        storesCount,
      });

      this.$router.push({ hash: '', query: this.$route.query }).catch(() => {});
      this.$nextTick(() => {
        this.$router.push({
          hash: name === DELIVERY_TYPES.postamat
            ? DELIVERY_TYPES.delivery
            : name,
          query: this.$route.query,
        });
      });
    },
  },
};
</script>


<style lang="scss">
.product-card {
  position: relative;
  > .ui-loader {
    position: absolute;
    top: 200px;
    left: 50%;
    transform: translate(-50%);
    z-index: 10;
    color: $blue;
    & ~ .content {
      opacity: .6;
      pointer-events: none;
      transition: opacity .3s;
    }
  }

  .content {
    transition: opacity .2s .1s;
  }

  .info-header {
    position: relative;
    margin-top: 12px;
    padding-right: 40px;

    .title {
      font-size: 12px;
      line-height: 16px;
      font-weight: normal;
      color: $gray;
      margin-right: 4px;

      & > span {
        font-size: 20px;
        line-height: 28px;
        color: $black;
        letter-spacing: -.2px;
      }
    }
    .favorite-button {
      position: absolute;
      top: 0;
      right: 0;
    }
  }

  .gallery {
    display: flex;
    flex-wrap: wrap;
    margin-top: 12px;

    .image-container {
      flex: 0 0 100%;
      margin-bottom: 16px;
      img {
        width: 100%;
      }
    }
  }

  .product-markers {
    margin-top: 24px;
  }

  .prices {
    margin-top: 20px;
    display: flex;
    align-items: center;

    .price {
      display: flex;
      font-weight: 600;
      font-size: 32px;
      line-height: 40px;

      &.discount {
        color: $red;
      }
    }

    .old-price {
      margin-left: 8px;
      color: $gray;
      font-weight: normal;
      font-size: 16px;
      line-height: 24px;
      text-decoration: line-through;
      transform: translateY(6px);
    }
  }

  .select-size {
    margin-top: 16px;
  }

  .product-card__to-cart {
    margin-top: 28px;
  }

  .button,
  .product-bonus {
    margin-top: 24px;
  }

  .select-size-label,
  .select-color-label {
    font-size: 12px;
    line-height: 16px;
    color: $gray;
    font-weight: normal;
  }

  .product-card__info {
    margin-top: 32px;
  }

  &__product-delivery {
    margin-top: 24px;
  }
}

@include breakpoint("tablet") {
  .product-card {
    .content {
      display: grid;
      /* stylelint-disable-next-line */
      grid-template-areas:
          "gallery info-header"
          "gallery info-content";
      grid-template-rows: auto 1fr;
      grid-template-columns: calc(51% - 16px) calc(49% - 16px);
      grid-column-gap: 32px;
      margin-top: 32px;

      .info-header {
        margin-top: 0;
        grid-area: info-header;

        .product-markers {
          margin-top: -4px;
        }
      }

      .gallery {
        grid-area: gallery;
        margin-top: 0;
      }

      .info-content {
        grid-area: info-content;

        .prices {
          margin-top: 8px;

          .old-price {
            margin-left: 10px;
          }
        }

        .select-color {
          margin-top: 16px;
        }

        .button {
          margin-top: 20px;
        }

        .product-bonus {
          margin-top: 24px;
        }
      }
    }

    .product-card__to-cart {
      margin-top: 24px;
    }
  }
}

@include breakpoint("desktop") {
  .product-card {
    .content {
      grid-template-columns: calc(67% - 20px) calc(33% - 20px);
      grid-column-gap: 40px;

      .gallery {
        .image-container {
          flex: 0 0 50%;
        }
      }
    }

    &.single-image {
      .content {
        grid-template-columns: calc(60% - 20px) calc(40% - 20px);
        .gallery {
          .image-container {
            flex: 0 0 100%;
          }
        }
      }
    }

    .product-markers {
      margin-bottom: 5px;
    }

    .info-header {
      .title {
        font-size: 14px;
        line-height: 20px;

        & > span {
          font-size: 24px;
          line-height: 32px;
          margin-right: 12px;
        }
      }
    }

    .info-content {
      img,
      .color-code {
        width: 28px;
        height: 28px;
      }
    }

    .prices {
      margin-top: 4px;

      .price {
        font-size: 40px;
        line-height: 48px;
      }

      .old-price {
        margin-left: 12px;
        font-size: 20px;
      }
    }

    .button,
    .product-bonus {
      margin-top: 32px;
    }

    .product-card__to-cart {
      margin-top: 32px;
    }

    .subtitle,
    .select-size-label,
    .select-color-label {
      font-size: 14px;
    }

    &__product-delivery {
      margin-top: 32px;
    }
  }
}
</style>
